import ReactGA from 'react-ga4';

// 'emulator' (the Playwright e2e suite running against the local Firebase
// Emulator Suite, see tools.firebase.js's USE_EMULATORS gate) deliberately
// does NOT get a tracking ID here, unlike 'development' - real Google
// Analytics beacons would otherwise fire on every e2e page load, and
// page.goto()/waitForURL()'s default `waitUntil: 'load'` waits for ALL
// resources (including this kind of third-party network call) to settle,
// so a slow/blocked GA endpoint would be a real, external source of test
// hangs/flakiness, not just noise in a real GA dashboard.
const getTrackingID = (env: string | undefined): string | null => {
  switch (env) {
    case 'production':
      return 'G-2M1J9NQWPK';
    case 'stage':
      return 'G-5CCHNJEQPQ';
    case 'development':
      return 'G-YL0XC7NKGR';
    case 'emulator':
      return null;
    default: {
      throw new Error(`[${env}] is an invalid environment.`);
    }
  }
};

const trackingID = getTrackingID(process.env.APP_ENV);
// Only initialize (and only ever call through to ReactGA below) when a
// real tracking ID exists - under 'emulator', trackingID is null and this
// whole module becomes a genuine no-op rather than a real-but-pointed-at-
// a-shared-dev-ID beacon.
if (trackingID) {
  ReactGA.initialize(trackingID);
}

type TrackerEvent = {
  category: string;
  action: string;
  [key: string]: unknown;
};

const Tracker = {
  set: (obj: Record<string, unknown>) => {
    if (trackingID) {
      ReactGA.set(obj);
    }
  },

  // react-ga4 has no dedicated .pageview() method (that was react-ga's
  // Universal Analytics-era API) - .send({ hitType: 'pageview', page }) is
  // its documented replacement, routing through the same underlying
  // gtag('event', 'page_view', { page_path: page }) call.
  pageview: (page: string) => {
    if (trackingID) {
      ReactGA.send({ hitType: 'pageview', page });
    }
  },

  event: (event: TrackerEvent) => {
    if (trackingID) {
      ReactGA.event(event);
    }
  },
};
Object.freeze(Tracker);

export default Tracker;
